import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Clock, MapPin, Send, Loader2 } from "lucide-react";
import { SectionHead, FadeIn } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", car_model: "", message: "" };

const INFO = [
  {
    icon: Phone,
    title: "Telefon",
    lines: ["+48 693 711 533"],
  },
  {
    icon: Clock,
    title: "Godziny otwarcia",
    lines: ["Codziennie: 7:00–20:00"],
  },
  {
    icon: MapPin,
    title: "Lokalizacja",
    lines: ["Opalenica"],
  },
];

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Wysłano! Odezwiemy się z darmową wyceną.");
      setForm(EMPTY);
    } catch {
      toast.error("Nie udało się wysłać — zadzwoń lub napisz później.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-surface px-4 py-3.5 text-sm text-zinc-100 outline-none transition-colors duration-300 focus:border-brand";

  return (
    <section
      id="kontakt"
      className="relative overflow-hidden py-24 md:py-36"
      data-testid="contact-section"
    >
      <div
        className="absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-brand/10 blur-[160px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 md:px-10 lg:grid-cols-2">
        <div>
          <SectionHead
            eyebrow="Kontakt"
            title={
              <>
                Darmowa <span className="text-brand">wycena</span>
              </>
            }
            desc="Napisz, czego oczekujesz — odpowiemy z propozycją sprzętu i wyceną montażu."
          />

          <FadeIn delay={0.1}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="mt-10 space-y-5 rounded-3xl border border-white/8 bg-surface/60 p-6 md:p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400"
                  >
                    Imię
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={set("name")}
                    required
                    minLength={2}
                    placeholder="Jan"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-phone"
                    className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400"
                  >
                    Telefon
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    required
                    minLength={7}
                    placeholder="+48 600 000 000"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="contact-car"
                  className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400"
                >
                  Marka i model auta
                </label>
                <input
                  id="contact-car"
                  data-testid="contact-input-car"
                  value={form.car_model}
                  onChange={set("car_model")}
                  required
                  minLength={2}
                  placeholder="np. Golf VII"
                  className={inputCls}
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400"
                >
                  E-mail (opcjonalnie)
                </label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="twoj@email.pl"
                  className={inputCls}
                />
              </div>
              <div>
                <label
                  htmlFor="contact-message"
                  className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400"
                >
                  Czego oczekujesz?
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  required
                  minLength={5}
                  rows={4}
                  placeholder="Chcę subwoofer do bagażnika i wygłuszenie drzwi..."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-all duration-300 hover:bg-brand-dark disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {sending ? "Wysyłanie..." : "Wyślij — darmowa wycena"}
              </button>
            </form>
          </FadeIn>
        </div>

        <div className="flex flex-col gap-5 lg:pt-[132px]">
          {INFO.map((info, i) => (
            <FadeIn key={info.title} delay={0.1 + i * 0.08}>
              <div
                data-testid={`contact-info-${i}`}
                className="group flex gap-6 rounded-3xl border border-white/8 bg-surface/60 p-7 transition-colors duration-500 hover:border-brand/50 md:p-8"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-brand/30 bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-ink">
                  <info.icon size={20} strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-white">
                    {info.title}
                  </h3>
                  {info.lines.map((l) => (
                    <p key={l} className="mt-1 text-sm text-zinc-400">
                      {l}
                    </p>
                  ))}
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
